// @flow
import React from 'react';
import { FocusedImage as FImage } from 'image-focus';
import AssetModel from 'Stores/AssetModel';
import classNames from 'classnames/bind';
import styles from './styles.scss';
import { useDebounce } from 'Utils/useDebounce';

const cx = classNames.bind(styles);
// declare var IMAGE_FALLBACK_URL: string;
declare var PUBLIC_BASE_URL: string;

//
// @see https://image-focus.stackblitz.io/
//

type Props = {
  image: AssetModel,
  imageMobile?: AssetModel,
  useThumb?: string,
  fullWidth?: boolean,
  ariaHidden?: string,
};

export default class FocusedImage extends React.Component<Props> {
  imageElm: ?HTMLImageElement;

  constructor(props: Props) {
    super(props);

    this.state = {
      useMobileImage: this.props.imageMobile && window.matchMedia('(max-width: 1024px)').matches,
    };
  }

  componentDidMount = () => {
    this.setImageWithFocus();
  };

  componentDidUpdate = () => {
    this.setImageWithFocus();
  };

  updateUseMobileImage = () => {
    const useMobileImage = window.matchMedia('(max-width: 1024px)').matches;

    if (useMobileImage !== this.state.useMobileImage) {
      this.setState({ useMobileImage });
    }
  };

  setImageWithFocus = () => {
    const { focus } = this.imageConfig();
    new FImage(this.imageElm, { focus });

    if (this.props.imageMobile) {
      window.addEventListener('resize', useDebounce({ func: this.updateUseMobileImage }));
    }
  };

  setRef = (elm: ?HTMLImageElement) => {
    this.imageElm = elm;
  };

  imageConfig = () => {
    const { image, imageMobile, useThumb } = this.props;
    const { useMobileImage } = this.state;

    let fallbackSrc = `${PUBLIC_BASE_URL}/img/fallback-image-new-logo-dark-blue.jpg`;
    let imageSrc = fallbackSrc;
    let imageAlt;
    let thumbnails = [];
    let focus = {
      x: 0,
      y: 0,
    };
    let role;

    if (image) {
      imageSrc = image.src;
      imageAlt = image.alt;
      thumbnails = image.thumbnails;
      focus = image.focus;
    }

    if (imageMobile && useMobileImage) {
      imageSrc = imageMobile.src;
      imageAlt = imageMobile.alt;
      thumbnails = imageMobile.thumbnails;
      focus = imageMobile.focus;
    }

    if (useThumb && thumbnails && thumbnails.length > 0) {
      const thumbnail = thumbnails[AssetModel.SIZE[useThumb]];

      if (thumbnail) {
        imageSrc = thumbnail;
      }
    }

    if (!imageAlt || imageAlt.trim() === '') {
      role = 'presentation';
      imageAlt = undefined;
    }

    return {
      focus,
      imageProps: {
        ref: this.setRef,
        src: imageSrc,
        onError: (e) => {
          e.target.onError = null;
          e.target.src = fallbackSrc;
        },
        alt: imageAlt,
        role,
      },
    };
  };

  render() {
    const { image, fullWidth, ariaHidden } = this.props;
    const { imageProps } = this.imageConfig();

    const rootClass = cx(
      'container',
      {
        fullWidth,
      },
      this.props.className
    );

    return (
      <div className={rootClass} aria-hidden={ariaHidden}>
        <img {...imageProps} />

        {image && image.dim > 0 && (
          <div
            className={styles.dim}
            style={{ backgroundColor: `rgba(25, 25, 25,${image.dim})` }}
          />
        )}
      </div>
    );
  }
}
